import React, { useEffect, useMemo } from 'react';

import { FetchError } from '../../../common/api/api';
import WorkflowStatusSelector, {
  WorkflowStatusSelectorProps
} from '../../workflow/components/WorkflowStatusSelector';
import {
  useFindWorkflowVersionByEntity,
  useWorkflowStatuses
} from '../../workflow/Workflow.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';

export type HazardEntityStatusFilterProps = {
  includeHazardStatus?: boolean;
} & Pick<WorkflowStatusSelectorProps, 'value' | 'onChange'>;

const HazardEntityStatusFilter: React.FC<HazardEntityStatusFilterProps> = (
  props: HazardEntityStatusFilterProps
) => {
  const { includeHazardStatus } = props;
  const hazard = useFindWorkflowVersionByEntity(WorkflowEntity.Hazard);
  const cause = useFindWorkflowVersionByEntity(WorkflowEntity.Cause);
  const control = useFindWorkflowVersionByEntity(WorkflowEntity.Control);
  const action = useFindWorkflowVersionByEntity(WorkflowEntity.Action);

  const error: FetchError | undefined =
    hazard.loadingError ||
    cause.loadingError ||
    control.loadingError ||
    action.loadingError;
  const isLoading: boolean =
    hazard.isLoading ||
    cause.isLoading ||
    control.isLoading ||
    action.isLoading;

  const hazardOption: string[] = useWorkflowStatuses(hazard.workflowVersion);
  const causeOptions: string[] = useWorkflowStatuses(cause.workflowVersion);
  const controlOptions: string[] = useWorkflowStatuses(control.workflowVersion);
  const actionOptions: string[] = useWorkflowStatuses(action.workflowVersion);

  const options: string[] = useMemo(() => {
    const merged: string[] = includeHazardStatus
      ? ([] as string[]).concat(
          hazardOption,
          causeOptions,
          controlOptions,
          actionOptions
        )
      : ([] as string[]).concat(causeOptions, controlOptions, actionOptions);
    return Array.from(new Set(merged));
  }, [hazardOption, causeOptions, controlOptions, actionOptions]);

  useEffect(() => {
    hazard.findWorkflowVersion();
    cause.findWorkflowVersion();
    control.findWorkflowVersion();
    action.findWorkflowVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hazard.findWorkflowVersion,
    cause.findWorkflowVersion,
    control.findWorkflowVersion,
    action.findWorkflowVersion
  ]);

  return (
    <WorkflowStatusSelector
      {...props}
      error={error}
      options={options}
      isLoading={isLoading}
      textFieldProps={{ label: undefined, placeholder: 'Select Status' }}
    />
  );
};

export default HazardEntityStatusFilter;
